/**
 * Tiny DOM helpers shared by the renderer, the sims and the chat panel.
 *
 * Deliberately small: there is no framework here, and no npm dependency is allowed in client
 * code. The one rule these exist to enforce is that text always arrives through `textContent`,
 * never through `innerHTML` — no string that came from the server or from a sandboxed frame is
 * ever parsed as markup.
 */

export interface ElOptions {
  class?: string;
  text?: string;
  attrs?: Record<string, string | number | boolean | undefined>;
  /** Inline style properties. Used sparingly — layout lives in public/style.css. */
  style?: Partial<Record<string, string>>;
}

export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  options: ElOptions = {},
  children: Array<Node | string | null | undefined> = [],
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (options.class) node.className = options.class;
  if (options.text !== undefined) node.textContent = options.text;
  if (options.attrs) {
    for (const [key, value] of Object.entries(options.attrs)) {
      if (value === undefined || value === false) continue;
      node.setAttribute(key, value === true ? "" : String(value));
    }
  }
  if (options.style) {
    for (const [key, value] of Object.entries(options.style)) {
      if (value !== undefined) node.style.setProperty(key, value);
    }
  }
  append(node, children);
  return node;
}

export function append(
  parent: Node,
  children: Array<Node | string | null | undefined>,
): void {
  for (const child of children) {
    if (child === null || child === undefined) continue;
    parent.appendChild(typeof child === "string" ? document.createTextNode(child) : child);
  }
}

/** A real <button>. Never a clickable div — screen readers and keyboards need the element. */
export function button(
  label: string,
  className: string,
  onClick: (ev: MouseEvent) => void,
  attrs: Record<string, string | number | boolean | undefined> = {},
): HTMLButtonElement {
  const b = el("button", {
    class: className,
    text: label,
    attrs: { type: "button", ...attrs },
  });
  b.addEventListener("click", onClick);
  return b;
}

export function clear(node: Node): void {
  while (node.firstChild) node.removeChild(node.firstChild);
}

/**
 * A short, readable paragraph split. The spec's `text` blocks are plain prose with a hard length
 * cap; the only structure we honour is a blank line meaning "new paragraph". Bold/italic markup is
 * intentionally *not* interpreted, because interpreting it would mean building markup from a
 * model-authored string.
 */
export function paragraphs(md: string): HTMLParagraphElement[] {
  return md
    .split(/\n{2,}/)
    .map((chunk) => chunk.trim())
    .filter((chunk) => chunk.length > 0)
    .map((chunk) => el("p", { text: chunk }));
}

/**
 * Deterministic shuffle. Presentation order has to be stable across re-renders — a child who
 * looks away and looks back should not find the options rearranged — so the seed is the block id
 * rather than Math.random().
 */
export function seededShuffle<T>(items: readonly T[], seed: string): T[] {
  const out = items.slice();
  let state = hash32(seed) || 1;
  const next = (): number => {
    // xorshift32 — small, deterministic, good enough for laying out six list items.
    state ^= state << 13;
    state ^= state >>> 17;
    state ^= state << 5;
    return (state >>> 0) / 0x100000000;
  };
  for (let i = out.length - 1; i > 0; i -= 1) {
    const j = Math.floor(next() * (i + 1));
    const a = out[i];
    const b = out[j];
    if (a === undefined || b === undefined) continue;
    out[i] = b;
    out[j] = a;
  }
  return out;
}

export function hash32(input: string): number {
  let h = 2166136261;
  for (let i = 0; i < input.length; i += 1) {
    h ^= input.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

export function clamp(value: number, min: number, max: number): number {
  return value < min ? min : value > max ? max : value;
}

/**
 * Number formatting that cannot contradict the lesson.
 *
 * principles.md: "a readout must never show 0 while the underlying value is non-zero." Rounding
 * a small non-zero quantity to "0" is exactly the failure that made the POC's energy meter lie,
 * so anything that would round to zero is rendered as "< 0.1" instead.
 */
export function fmt(value: number, digits = 1): string {
  if (!Number.isFinite(value)) return "—";
  if (value === 0) return "0";
  const smallest = Math.pow(10, -digits);
  if (Math.abs(value) < smallest) return `${value < 0 ? "> -" : "< "}${smallest.toFixed(digits)}`;
  return value.toFixed(digits);
}

/** Percentage with the same no-false-zero rule, for meters. */
export function fmtPercent(fraction: number): string {
  if (!Number.isFinite(fraction)) return "—";
  if (fraction === 0) return "0%";
  const pct = fraction * 100;
  if (pct > 0 && pct < 0.1) return "< 0.1%";
  if (pct >= 99.95 && pct < 100) return "99.9%";
  return `${pct.toFixed(pct >= 10 ? 0 : 1)}%`;
}

export function prefersReducedMotion(): boolean {
  return (
    typeof matchMedia === "function" && matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

/** Debounce that keeps the latest call's arguments. */
export function debounce<A extends unknown[]>(
  fn: (...args: A) => void,
  ms: number,
): (...args: A) => void {
  let timer: number | undefined;
  return (...args: A): void => {
    if (timer !== undefined) clearTimeout(timer);
    timer = setTimeout(() => {
      timer = undefined;
      fn(...args);
    }, ms) as unknown as number;
  };
}

/** Cap a possibly-hostile string before it goes anywhere near telemetry. */
export function cap(value: unknown, max: number): string {
  return typeof value === "string" ? value.slice(0, max) : "";
}
